import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Card } from "./Card";
import { Sparkline } from "./Sparkline";

export type KpiTone = "primary" | "primary2" | "success" | "warning" | "destructive";

/** The tile's accent, as a colour expression rather than a class: the same value
 *  paints the icon square, the delta pill and the sparkline. */
const TONE: Record<KpiTone, string> = {
  primary: "var(--color-primary)",
  primary2: "var(--color-primary-2)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  destructive: "var(--color-destructive)",
};

/**
 * The target this figure is measured against, when the desk has set one.
 *
 * Already reduced to a percentage and a caption rather than carrying the raw
 * figure and the raw target. The tile cannot do that arithmetic itself: `value`
 * is a ReactNode - a rolling counter, a formatted sum, whatever the screen wants
 * - and there is no honest way to get a number back out of it. Passing the
 * result also keeps the tile out of the question of what "progress" means for a
 * given metric, which differs by metric and belongs to the caller.
 */
export interface KpiGoal {
  /** Progress as a percentage. May exceed 100 - the BAR is clamped, the text is not. */
  pct: number;
  /**
   * The one-line "5 200 / 8 000" under the bar.
   *
   * A plain string rather than a node, because it is also the bar's accessible
   * name. A node would have to be described twice - once for the eye and once
   * for the screen reader - and the two would drift.
   */
  foot: string;
}

export interface KpiProps {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  tone?: KpiTone;
  /** Percent change against the previous period. Omitted when there is nothing
   *  to compare against - a "0%" on a first month is a claim, not a fact. */
  delta?: number | null;
  /** What the delta is measured against, or any other one-line footnote. */
  foot?: ReactNode;
  series?: number[];
  /** The target, when one is set. Null or absent draws the tile exactly as it
   *  was before targets existed - no bar, no extra line, same height. */
  goal?: KpiGoal | null;
}

export function Kpi({ icon: Icon, label, value, tone = "primary", delta, foot, series, goal }: KpiProps) {
  const color = TONE[tone];
  const up = (delta ?? 0) >= 0;
  // Clamped for the bar only. A month at 150% must not paint outside the track,
  // but the figure beside it still says 150 - see `KpiGoal.pct`.
  const fill = goal ? Math.max(0, Math.min(100, goal.pct)) : 0;
  const reached = goal ? goal.pct >= 100 : false;
  return (
    <Card className="crm-kpi">
      <div className="crm-kpi-top">
        <span
          className="crm-kpi-tile"
          style={{
            background: `color-mix(in oklab, ${color} 13%, transparent)`,
            color: tone === "warning" ? `color-mix(in oklab, ${color} 55%, var(--color-foreground))` : color,
          }}
        >
          <Icon className="size-[18px]" strokeWidth={1.7} />
        </span>
        <span className="crm-kpi-lab">{label}</span>
      </div>
      <div className="crm-kpi-val">{value}</div>
      {goal ? (
        <div className="crm-kpi-goal">
          {/* `role="img"` with a label rather than a progressbar role: this is a
              static picture of a finished measurement, not a control or a task
              in flight, and a screen reader announcing "progressbar" would
              promise something that updates. The label carries the same two
              numbers the sighted reader gets. */}
          <span className="crm-kpi-goal-track" role="img" aria-label={goal.foot}>
            <span
              className="crm-kpi-goal-fill"
              style={{
                width: `${fill}%`,
                background: reached ? "var(--color-success)" : color,
              }}
            />
          </span>
          <span className={reached ? "crm-kpi-goal-foot is-hit" : "crm-kpi-goal-foot"}>{goal.foot}</span>
        </div>
      ) : null}
      <div className="crm-kpi-foot">
        {delta === null || delta === undefined ? null : (
          <span className={up ? "crm-dlt crm-dlt-up" : "crm-dlt crm-dlt-dn"}>
            {up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
            {Math.abs(delta).toLocaleString("et-EE", { maximumFractionDigits: 1 })}%
          </span>
        )}
        {foot}
      </div>
      {series && series.length > 1 ? <Sparkline values={series} color={color} /> : null}
    </Card>
  );
}
